import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadPageContent, handlePageLinkClick } from '../utils/pageHelpers';
import { PageSkeleton } from '../components/Loading';

interface InspectionPageProps {
  subpage?: string;
}

const InspectionPage: React.FC<InspectionPageProps> = ({ subpage = 'c1' }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const fullContent = await loadPageContent(`/cloneTarget/www.keso.kr/check/${subpage}.html`);
        setContent(fullContent);
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [subpage]);

  useEffect(() => {
    if (!contentRef.current) return;

    const contentElement = contentRef.current;
    const linkClickHandler = (e: Event) => handlePageLinkClick(e, navigate);

    contentElement.addEventListener('click', linkClickHandler, true);
    window.scrollTo(0, 0);

    // Execute inline scripts in the content
    const scripts = contentElement.querySelectorAll('script');

    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');

      // Copy script attributes
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy script content
      newScript.textContent = oldScript.textContent;

      // Replace old script with new script to execute it
      if (oldScript.parentNode) {
        oldScript.parentNode.replaceChild(newScript, oldScript);
      }
    });

    // Initialize tab functionality with vanilla JavaScript (no jQuery dependency)
    const initTabs = () => {
      // Helper function to toggle expand/collapse
      const setupToggle = (buttonClass: string, contentId: string, collapsedHeight: string) => {
        const buttons = document.querySelectorAll(`.${buttonClass}`);
        const content = document.getElementById(contentId);

        if (!content || buttons.length === 0) {
          return;
        }

        // Set initial collapsed state
        content.style.height = collapsedHeight;
        content.style.overflow = 'hidden';
        content.style.transition = 'height 0.4s ease';

        buttons.forEach((button) => {
          // Remove any existing listeners
          const newButton = button.cloneNode(true) as HTMLElement;
          button.parentNode?.replaceChild(newButton, button);

          newButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isExpanded = content.classList.contains('toggle');

            if (isExpanded) {
              // Collapse
              content.classList.remove('toggle');
              content.style.height = collapsedHeight;
              newButton.textContent = '법률전문 보기';
            } else {
              // Expand
              const autoHeight = content.scrollHeight + 'px';

              content.classList.add('toggle');
              content.style.height = autoHeight;
              newButton.textContent = '법률전문 닫기';
            }

            return false;
          });
        });
      };

      // Setup all three toggles
      setupToggle('lopen_01', 'expandDiv_01', '140px');
      setupToggle('lopen_02', 'expandDiv_02', '180px');
      setupToggle('lopen_03', 'expandDiv_03', '180px');
    };

    // Initialize after a short delay to ensure DOM is ready
    const timer = setTimeout(initTabs, 100);

    return () => {
      contentElement.removeEventListener('click', linkClickHandler, true);
      clearTimeout(timer);
    };
  }, [content, navigate]);

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content }} />
  );
};

export default InspectionPage;
